import type { SetupMangoPayDirectDebitBody } from '@pflegenavi/frontend/api-nursing-home';
import {
  useCancelFamilyMemberMandate,
  useCancelResidentMandate,
  useGetFamilyMemberResident,
  useGetGetFamilyMemberCountries,
  useGetMangoPayDirectDebit,
  useGetMangoPayDirectDebitResident,
  useSetupMangoPayDirectDebit,
  useSetupMangoPayDirectDebitResident,
  useShowResidentForFamilyMemberPhoenix,
  useUpdateFamilyMemberResident,
  useGetPaymentInfoForFamilyMemberPhoenix,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  getActiveBalanceId,
  getSelectedBalance,
} from '@pflegenavi/shared-frontend/platform';
import {
  MangopayBankAccountOwner,
  PaymentInitiatedBy,
  PendingChargeStatus,
  ResidentState,
} from '@pflegenavi/shared/api';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInitializeFM } from '../hooks/useInitializeFM';
import { MangopayDirectDebitPageView } from './MangopayDirectDebitPageView';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface MangopayDirectDebitPageProps {}

export const MangopayDirectDebitPage: FC<MangopayDirectDebitPageProps> = () => {
  const { t } = useTranslation();
  const { selectedResident } = useInitializeFM();
  const { enqueueSnackbar } = useSnackbar();

  // State for modals
  const [showSwitchBankAccountModal, setShowSwitchBankAccountModal] =
    useState(false);

  const { data: countries } = useGetGetFamilyMemberCountries({
    tenant: selectedResident?.tenant,
  });

  const {
    data: residentFamilyMember,
    isLoading: isResidentFamilyMemberLoading,
  } = useGetFamilyMemberResident({
    tenant: selectedResident?.tenant ?? '',
    resident_id: selectedResident?.residentId ?? '',
  });

  const { mutateAsync: updateFamilyMemberResident } =
    useUpdateFamilyMemberResident({
      resident_id: selectedResident?.residentId ?? '',
      tenant: selectedResident?.tenant ?? '',
    });

  // This is the mandate of the family member
  const { data: familyMemberMandate, isLoading: isFamilyMemberMandateLoading } =
    useGetMangoPayDirectDebit({
      paymentProcessorMode:
        selectedResident?.tenantPaymentProcessorMode ?? 'live',
    });

  const { data: residentMandate, isLoading: isResidentMandateLoading } =
    useGetMangoPayDirectDebitResident({
      paymentProcessorMode:
        selectedResident?.tenantPaymentProcessorMode ?? 'live',
      tenant: selectedResident?.tenant ?? '',
      resident_id: selectedResident?.residentId ?? '',
    });

  const { mutateAsync: setupDirectDebitFamilyMember } =
    useSetupMangoPayDirectDebit({
      paymentProcessorMode: (selectedResident?.tenantPaymentProcessorMode ??
        'live') as 'live' | 'test',
    });

  const { mutateAsync: setupDirectDebitResident } =
    useSetupMangoPayDirectDebitResident();

  const { data: residentResultPhoenix, isPending: isResidentPending } =
    useShowResidentForFamilyMemberPhoenix(
      selectedResident?.residentId ?? '',
      selectedResident?.tenant ?? ''
    );

  const activeBalance = useMemo(() => {
    const activeBalanceId = getActiveBalanceId(
      residentResultPhoenix?.data.resident
    );
    return getSelectedBalance(residentResultPhoenix?.data, activeBalanceId);
  }, [residentResultPhoenix]);

  // Check resident states for disabling functionality
  const isResidentExited =
    residentResultPhoenix?.data.resident.state === ResidentState.exited;
  const isResidentArchived =
    residentResultPhoenix?.data.resident.state === ResidentState.archived;
  const isResidentAwaitingSettlement =
    residentResultPhoenix?.data.resident.state ===
    ResidentState.awaitingSettlement;

  // Determine what to disable based on resident state
  const isPageDisabled = isResidentArchived || isResidentAwaitingSettlement;
  const isSepaFormDisabled = isResidentExited;

  // Fetch payment info to check for failed payments
  const { data: paymentInfoPhoenix } = useGetPaymentInfoForFamilyMemberPhoenix(
    selectedResident?.residentId ?? '',
    selectedResident?.tenant ?? ''
  );

  // Check if there's a failed/frozen charge initiated by family member
  const hasFailedPayment = useMemo(() => {
    const pendingCharges = paymentInfoPhoenix?.pending_charges ?? [];
    return pendingCharges.some(
      (charge) =>
        (charge.pending_charge_status === PendingChargeStatus.failed ||
          charge.pending_charge_status === PendingChargeStatus.frozen) &&
        charge.payment_initiation === PaymentInitiatedBy.FamilyMember
    );
  }, [paymentInfoPhoenix?.pending_charges]);

  // Get payment initiation type
  const paymentInitiation =
    paymentInfoPhoenix?.payment_settings?.payment_initiation;

  // Check if manual payments are disabled
  const isPaymentDisabled = paymentInitiation === PaymentInitiatedBy.Disabled;

  // Define mandate before using it in setOwner
  const mandate = useMemo(() => {
    if (residentFamilyMember?.data?.source_of_mandate === 'resident') {
      return residentMandate;
    } else {
      return familyMemberMandate;
    }
  }, [
    familyMemberMandate,
    residentFamilyMember?.data?.source_of_mandate,
    residentMandate,
  ]);

  const confirmOwnerChange = useCallback(() => {
    // Always use 'resident' as the source of mandate
    return updateFamilyMemberResident({
      source_of_mandate: 'resident',
    }).then(() => {
      enqueueSnackbar(
        t(
          'mangopay.direct-debit.setup.bank-account-owner.owner-change-success'
        ),
        { variant: 'success' }
      );
      setShowSwitchBankAccountModal(false);
      return undefined;
    });
  }, [enqueueSnackbar, t, updateFamilyMemberResident]);

  const cancelOwnerChange = useCallback(() => {
    setShowSwitchBankAccountModal(false);
  }, []);

  const setupMandate = useCallback(
    (body: SetupMangoPayDirectDebitBody) => {
      if (residentFamilyMember?.data?.source_of_mandate === 'resident') {
        return setupDirectDebitResident({
          body: body,
          params: {
            tenant: selectedResident?.tenant ?? '',
            resident_id: selectedResident?.residentId ?? '',
            paymentProcessorMode:
              selectedResident?.tenantPaymentProcessorMode ?? 'live',
          },
        });
      } else {
        return setupDirectDebitFamilyMember(body);
      }
    },
    [
      residentFamilyMember?.data?.source_of_mandate,
      selectedResident?.residentId,
      selectedResident?.tenant,
      selectedResident?.tenantPaymentProcessorMode,
      setupDirectDebitFamilyMember,
      setupDirectDebitResident,
    ]
  );

  const mandateActive = mandate?.data?.mandate?.status === 'active';

  const isLoading =
    isResidentFamilyMemberLoading ||
    isResidentMandateLoading ||
    isFamilyMemberMandateLoading;

  // Always set owner to Resident
  const owner = MangopayBankAccountOwner.Resident;

  // Update resident family member to use 'resident' as source of mandate if it's not already set
  React.useEffect(() => {
    if (residentFamilyMember?.data?.source_of_mandate !== 'resident') {
      updateFamilyMemberResident({ source_of_mandate: 'resident' });
    }
  }, [
    residentFamilyMember?.data?.source_of_mandate,
    updateFamilyMemberResident,
  ]);

  const isFamilyMemberOwner = false; // Always false since owner is always Resident

  // Determine which cancel hook to use based on source_of_mandate
  const useDeleteDirectDebitMandate = (
    residentFamilyMember?.data?.source_of_mandate === 'resident'
      ? useCancelResidentMandate
      : useCancelFamilyMemberMandate
  ) as typeof useCancelResidentMandate;

  return (
    <MangopayDirectDebitPageView
      countries={countries?.data ?? []}
      mandate={mandate}
      activeBalance={activeBalance}
      isLoading={isLoading}
      isResidentPending={isResidentPending}
      mandateActive={mandateActive}
      owner={owner}
      isFamilyMemberOwner={isFamilyMemberOwner}
      setupMandate={setupMandate}
      confirmOwnerChange={confirmOwnerChange}
      cancelOwnerChange={cancelOwnerChange}
      showSwitchBankAccountModal={showSwitchBankAccountModal}
      useDeleteDirectDebitMandate={useDeleteDirectDebitMandate}
      hasFailedPayment={hasFailedPayment}
      isPageDisabled={isPageDisabled}
      isSepaFormDisabled={isSepaFormDisabled}
      paymentInitiation={paymentInitiation}
      isPaymentDisabled={isPaymentDisabled}
    />
  );
};
